import type { PartialMessage, PlainMessage } from '@bufbuild/protobuf';

import type { DocumentAnswerChange } from '@/shared/api/protocol-ts/api/scribe/svc_encounter_pb';
import { DentalNotationFormat } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import {
  type NoteTeethGroup,
  type NoteBlock,
  type NoteLine,
  type NoteList,
  type NoteNumberValue,
  type NoteTableRowValue,
  type NoteToothMark,
} from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';
import {
  DocumentAnswerSource,
  NoteAIBasis,
  NoteLineStatus,
  NoteToothNotation,
  type DocumentAnswer,
  type DocumentAnswers as NoteAnswers,
  type NoteAnswerTableRow,
  type NoteLineView,
} from '@/shared/api/protocol-ts/model/dto_scribe_encounter_pb';

// An AI value, mark or table cell assumed or suggested waits for the
// clinician; once confirmed it stays the AI's as an ordinary AI value.
export const isWaitingAiHint = ({
  AIBasis,
  AIConfirmed,
}: {
  AIBasis: NoteAIBasis;
  AIConfirmed: boolean;
}) =>
  !AIConfirmed &&
  (AIBasis === NoteAIBasis.NoteAIBasisAssumed ||
    AIBasis === NoteAIBasis.NoteAIBasisSuggested);

import {
  listNoteBlockOptions,
  noteOptionValue,
  type DocumentTemplate,
} from '@/entities/templates';
import { getDisplayToothNumber } from '@/entities/tooth';

import { type PendingAnswer } from '@/features/audioRecording';

import { NOTE_NONE_WORDS } from '../config';

import { joinProtocolValues } from './templateDraft';

// Thin view models over scribe's NoteView: the client lays the note out and
// reads which values are chosen; visibility, status, resolution and every
// printed text come from the view.

export type NoteBlockDef = PlainMessage<NoteBlock>;

export type NoteLineDef = PlainMessage<NoteLine>;

export type NoteLineRef = { line: NoteLineDef; sectionId: string };

// Template blocks and the blocks added on this note, by id.
export const indexNoteBlocks = (
  template: DocumentTemplate,
  answers: NoteAnswers | undefined,
): Map<string, NoteBlockDef> =>
  new Map<string, NoteBlockDef>([
    ...template.sections.flatMap((section) =>
      section.Lines.flatMap((line) =>
        line.Blocks.map((block): [string, NoteBlockDef] => [block.ID, block]),
      ),
    ),
    ...(answers?.AddedLines ?? []).flatMap((added) =>
      (added.Line?.Blocks ?? []).map((block): [string, NoteBlockDef] => [
        block.ID,
        block,
      ]),
    ),
    ...(answers?.AddedBlocks ?? []).flatMap((added) =>
      added.Block
        ? [[added.Block.ID, added.Block] as [string, NoteBlockDef]]
        : [],
    ),
  ]);

export const indexNoteLines = (
  template: DocumentTemplate,
  answers: NoteAnswers | undefined,
): Map<string, NoteLineRef> =>
  new Map<string, NoteLineRef>([
    ...template.sections.flatMap((section) =>
      section.Lines.map((line): [string, NoteLineRef] => [
        line.ID,
        { line, sectionId: section.ID },
      ]),
    ),
    ...(answers?.AddedLines ?? []).flatMap((added) =>
      added.Line
        ? [
            [
              added.Line.ID,
              { line: added.Line, sectionId: added.SectionID },
            ] as [string, NoteLineRef],
          ]
        : [],
    ),
  ]);

export const findNoteAnswer = (
  answers: NoteAnswers | undefined,
  blockId: string,
): DocumentAnswer | undefined =>
  answers?.Answers.find((answer) => answer.Key?.BlockID === blockId);

export const getNoteSectionTitle = (
  template: DocumentTemplate,
  sectionId: string,
) => template.sections.find((section) => section.ID === sectionId)?.Title ?? '';

// Included and unresolved lines are the note; deleted and skipped ones can
// be brought back; hidden ones wait for their link.
export const isNoteLineShown = ({ Status }: NoteLineView) =>
  Status === NoteLineStatus.NoteLineStatusIncluded ||
  Status === NoteLineStatus.NoteLineStatusUnresolved;

// A template line the after-Stop AI step removed stays in place, struck out
// and highlighted, until the clinician brings it back or leaves it removed.
export const isAiRemovedNoteLine = ({ Status, DeletedBy }: NoteLineView) =>
  Status === NoteLineStatus.NoteLineStatusDeleted &&
  DeletedBy === DocumentAnswerSource.DocumentAnswerSourceAI;

export const isNoteLineSetAside = (line: NoteLineView) =>
  (line.Status === NoteLineStatus.NoteLineStatusDeleted &&
    !isAiRemovedNoteLine(line)) ||
  line.Status === NoteLineStatus.NoteLineStatusSkipped;

// Guidance written into fixed wording in brackets ("[only if an implant was
// placed]") is read by the model only; the note never shows it.
export const stripInlineGuidance = (text: string) =>
  text.replace(/\s*\[[^\]]*\]/g, '');

// A locator for lists of lines (unresolved, set aside): the template wording
// with every value block as a gap. It is never note text.
export const describeNoteLine = (line: NoteLineDef | undefined) =>
  (line?.Blocks ?? [])
    .map((block) =>
      block.Kind.case === 'Text'
        ? stripInlineGuidance(block.Kind.value.Value).trim()
        : '…',
    )
    .filter(Boolean)
    .join(' ');

// What an empty value block of a line reads as where the line is named
// without its values: its label, else its options, so lines that share their
// wording can be told apart.
export const nameNoteBlock = (block: NoteBlockDef) => {
  const label = getNoteBlockLabel(block, []);
  if (label) {
    return label;
  }
  switch (block.Kind.case) {
    case 'Choice':
    case 'MultiChoice':
      return block.Kind.value.Values.join(' / ') || '…';
    default:
      return '…';
  }
};

// A line named by its wording and what its value blocks ask for, as the
// list of lines removed or skipped shows a line with no values.
export const nameNoteLine = (line: NoteLineDef | undefined) =>
  (line?.Blocks ?? [])
    .map((block) =>
      block.Kind.case === 'Text'
        ? stripInlineGuidance(block.Kind.value.Value).trim()
        : nameNoteBlock(block),
    )
    .filter(Boolean)
    .join(' ');

export type UnresolvedNoteLine = {
  sectionId: string;
  sectionTitle: string;
  lineId: string;
  label: string;
};

export const listUnresolvedNoteLines = (
  template: DocumentTemplate,
  answers: NoteAnswers | undefined,
): UnresolvedNoteLine[] => {
  const lines = indexNoteLines(template, answers);
  return (answers?.View?.Sections ?? []).flatMap((section) =>
    section.Lines.filter(
      (line) => line.Status === NoteLineStatus.NoteLineStatusUnresolved,
    ).map((line) => ({
      sectionId: section.SectionID,
      sectionTitle: getNoteSectionTitle(template, section.SectionID),
      lineId: line.LineID,
      label: describeNoteLine(lines.get(line.LineID)?.line),
    })),
  );
};

// Values compare as scribe compares them: ignoring case and spacing.
export const normalizeNoteValue = (value: string) =>
  value.trim().replace(/\s+/g, ' ').toLocaleLowerCase();

// The offered value a typed text names, by the value or by another of its
// spellings (an option's label or print). Scribe stores such own text as
// that value, which may reveal linked lines.
export const findNamedNoteValue = (
  offered: string[],
  text: string,
  spellings: (value: string) => string[] = () => [],
) =>
  offered.find((value) =>
    [value, ...spellings(value)].some(
      (spelling) => normalizeNoteValue(spelling) === normalizeNoteValue(text),
    ),
  );

// How a field's explicit none prints, as scribe prints it: the field's none
// label, else the template language's word for none.
export const printNoteNone = (
  { NoneLabel }: { NoneLabel?: string },
  language = '',
) => NoneLabel || NOTE_NONE_WORDS[language.trim().toLowerCase()] || '';

// The values a block that takes several holds. An own text (an edited
// printed value, the AI's Other text) is the last of them, as render prints
// it, so a change sends it with the values and keeps that order.
export const listHeldNoteValues = ({
  values,
  other,
}: Pick<NoteBlockSelection, 'values' | 'other'>) =>
  other && !values.includes(other) ? [...values, other] : values;

// The options "Other" added to a block in this note, in the order added.
export const findNoteOwnOptions = (
  answers: NoteAnswers | undefined,
  blockId: string,
): string[] =>
  answers?.OwnOptions.find((entry) => entry.BlockID === blockId)?.Values ?? [];

// The values a choice-like block offers: its own, or its list's items.
export const getNoteBlockValues = (
  block: NoteBlockDef,
  lists: NoteList[],
): string[] | null => {
  switch (block.Kind.case) {
    case 'Choice':
    case 'MultiChoice': {
      const options = listNoteBlockOptions(block);
      return options.length
        ? options.map(noteOptionValue)
        : block.Kind.value.Values;
    }
    case 'ListRef': {
      const { ListID } = block.Kind.value;
      const list = lists.find((item) => item.ID === ListID);
      return list ? list.Items.map((item) => item.Value) : null;
    }
    default:
      return null;
  }
};

// The hint an empty value block shows; a list block falls back to its list's
// name. It is never printed.
export const getNoteBlockLabel = (
  block: NoteBlockDef,
  lists: NoteList[],
): string | undefined => {
  switch (block.Kind.case) {
    case 'Choice':
    case 'MultiChoice':
    case 'Teeth':
    case 'FreeText':
    case 'Date':
    case 'Number':
    case 'Table':
      return block.Kind.value.Label;
    case 'ListRef': {
      const { Label, ListID } = block.Kind.value;
      return Label ?? lists.find((list) => list.ID === ListID)?.Name;
    }
    default:
      return undefined;
  }
};

export const isNoteBlockMultiple = (block: NoteBlockDef) => {
  switch (block.Kind.case) {
    case 'MultiChoice':
      return true;
    case 'ListRef':
      return !!block.Kind.value.Multiple;
    case 'Teeth':
      return block.Kind.value.Multiple !== false;
    default:
      return false;
  }
};

type NoteOptionBlockDef = NoteBlockDef & {
  Kind: Extract<
    NoteBlockDef['Kind'],
    { case: 'Choice' | 'MultiChoice' | 'ListRef' }
  >;
};

// Choice, multi-choice and list blocks: the blocks that offer options.
export const isNoteOptionBlock = (
  block: NoteBlockDef,
): block is NoteOptionBlockDef =>
  block.Kind.case === 'Choice' ||
  block.Kind.case === 'MultiChoice' ||
  block.Kind.case === 'ListRef';

export const isNoteOtherAllowed = (block: NoteBlockDef) =>
  isNoteOptionBlock(block) && block.Kind.value.OtherAllowed;

export const formatNoteTooth = (
  fdi: string,
  notation: NoteToothNotation | undefined,
) =>
  String(
    getDisplayToothNumber(
      Number(fdi),
      notation === NoteToothNotation.NoteToothNotationUniversal
        ? DentalNotationFormat.DentalNotationFormatUniversal
        : DentalNotationFormat.DentalNotationFormatISO,
    ),
  );

const withoutRepeats = <T>(items: T[]) =>
  items.filter((item, index) => items.indexOf(item) === index);

// A teeth value as scribe's render prints it (note_render.go valueText):
// groups in enum order, then teeth in FDI order shown in the note's
// notation, joined by ", ". The caller names the groups in the template
// language.
export const printNoteTeeth = ({
  teeth,
  groups,
  notation,
  groupName,
}: {
  teeth: string[];
  groups: NoteTeethGroup[];
  notation: NoteToothNotation | undefined;
  groupName: (group: NoteTeethGroup) => string;
}) =>
  joinProtocolValues([
    ...withoutRepeats(groups)
      .sort((left, right) => left - right)
      .map(groupName),
    ...withoutRepeats(teeth)
      .sort()
      .map((tooth) => formatNoteTooth(tooth, notation)),
  ]);

// An option value as scribe's render prints it (note_render.go valueText):
// the chosen values in template order, then the own values in the order
// added, then the own text, joined by ", ".
export const printNoteChoice = ({
  values,
  other,
}: {
  values: string[];
  other: string;
}) => joinProtocolValues([...values, other].filter(Boolean));

export type NoteBlockSelection = {
  values: string[];
  other: string;
  teeth: string[];
  groups: NoteTeethGroup[];
  // A date field's calendar date, YYYY-MM-DD.
  date: string;
  // A teeth field that stores marks: its marks.
  marks?: PlainMessage<NoteToothMark>[];
  // The explicit "none".
  none?: boolean;
  // A number field's values, in its qualifier order.
  numbers?: PlainMessage<NoteNumberValue>[];
  // A table field's rows with their cells.
  tableRows?: PlainMessage<NoteTableRowValue>[];
};

// The rows of a table answer without who set each cell.
export const readNoteTableRows = (
  rows: Pick<NoteAnswerTableRow, 'Row' | 'Cells'>[],
): PlainMessage<NoteTableRowValue>[] =>
  rows.map(({ Row, Cells }) => ({
    Row,
    Cells: Cells.map(({ Column, Value }) => ({ Column, Value })),
  }));

const EMPTY_SELECTION: NoteBlockSelection = {
  values: [],
  other: '',
  teeth: [],
  groups: [],
  date: '',
};

// What a block shows as chosen: the clinician's unsent value first, then the
// stored answer. Release, revert and the own-option changes wait for the
// server, which knows the answer they leave.
export const readNoteBlockSelection = (
  answer: DocumentAnswer | undefined,
  pending: PartialMessage<DocumentAnswerChange> | undefined,
): NoteBlockSelection => {
  if (pending?.Clear) {
    return EMPTY_SELECTION;
  }
  const settled =
    !pending ||
    pending.Release ||
    pending.Revert ||
    pending.ApplyCorrection ||
    pending.DismissCorrection ||
    pending.AddOwnOption ||
    pending.RemoveOwnOption;
  if (!settled) {
    return {
      values: pending.Values ?? [],
      other: pending.Text ?? '',
      teeth: pending.Teeth ?? [],
      groups: pending.TeethGroups ?? [],
      date: pending.Date ?? '',
      marks: (pending.Marks ?? []) as PlainMessage<NoteToothMark>[],
      none: !!pending.None,
      numbers: (pending.Numbers ?? []) as PlainMessage<NoteNumberValue>[],
      tableRows: (pending.TableRows ?? []) as PlainMessage<NoteTableRowValue>[],
    };
  }
  return answer && !answer.Cleared
    ? {
        values: answer.Values,
        other: answer.Text,
        teeth: answer.Teeth,
        groups: answer.TeethGroups,
        date: answer.Date ?? '',
        marks: answer.Marks.flatMap(({ Mark }) => (Mark ? [Mark] : [])),
        none: !!answer.None,
        numbers: answer.Numbers,
        tableRows: readNoteTableRows(answer.TableRows),
      }
    : EMPTY_SELECTION;
};

// Edits of one block share a key, so an unsent edit is replaced by the next.
export const noteBlockEditKey = (blockId: string) => `block:${blockId}`;

// The unsent change of one block, if the clinician made one: its own edit,
// else its part of an operation on several blocks.
export const findPendingNoteChange = (
  pending: Record<string, PendingAnswer>,
  blockId: string,
) => {
  const entry = pending[noteBlockEditKey(blockId)];
  if (entry) {
    return 'note' in entry ? entry.note.Changes?.[0] : undefined;
  }
  return Object.values(pending)
    .flatMap((item) => ('note' in item ? item.note.Changes ?? [] : []))
    .filter((change) => change.Key?.BlockID === blockId)
    .at(-1);
};
